import DOMPurify from 'dompurify'
import { parseVideoUrl } from '@/lib/videoEmbed'

// Matches http/https URLs in plain text
export const URL_REGEX = /https?:\/\/[^\s<]+/g

// Configure DOMPurify to allow specific tags and attributes.
// <iframe> is allowed only as a YouTube/Vimeo player: keepOnlyVideoIframes
// below drops every other one, mirroring HelpCenterHelper::ArticleScrubber.
const ALLOWED_TAGS = [
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  'code',
  'pre',
  'h1',
  'h2',
  'h3',
  'a',
  'img',
  'ul',
  'ol',
  'li',
  'blockquote',
  'span',
  'iframe',
]

const ALLOWED_ATTR = [
  'href',
  'src',
  'alt',
  'title',
  'target',
  'rel',
  'class',
  'data-variable',
  'data-key',
  'data-fallback',
  'data-label',
  'allowfullscreen',
  'loading',
]

DOMPurify.addHook('uponSanitizeElement', (currentNode) => {
  if (currentNode.nodeName !== 'IFRAME') return
  const node = currentNode as Element
  const embed = parseVideoUrl(node.getAttribute('src') || '')
  if (embed) {
    node.setAttribute('src', embed.embedUrl)
  } else {
    node.remove()
  }
})

/**
 * Sanitize HTML content to prevent XSS attacks
 * Allows common formatting tags, variable spans and YouTube/Vimeo players
 */
export function sanitizeHtml(html: string): string {
  if (!html) return ''

  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: true,
    ADD_ATTR: ['target'],
  })
}

/**
 * Check if content appears to be HTML
 * Used to decide whether to render as HTML or plain text
 */
export function isHtmlContent(content: string): boolean {
  if (!content) return false

  // Check for common HTML tags
  const htmlPattern = /<(p|br|a|img|strong|em|b|i|h[1-6]|ul|ol|li|pre|code|blockquote|span|iframe)[^>]*>/i
  return htmlPattern.test(content)
}

/**
 * Convert plain text to HTML (preserving line breaks)
 */
export function textToHtml(text: string): string {
  if (!text) return ''

  // Escape HTML entities
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')

  // Convert line breaks to <br>
  return escaped.replace(/\n/g, '<br>')
}

/**
 * Walk text nodes in HTML and convert plain URLs to <a> links.
 * URLs already inside <a> tags are left untouched.
 */
export function linkifyHtml(html: string): string {
  if (!html) return ''

  const container = document.createElement('div')
  container.innerHTML = html

  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT)
  const textNodes: Text[] = []

  while (walker.nextNode()) {
    textNodes.push(walker.currentNode as Text)
  }

  for (const node of textNodes) {
    if (node.parentElement?.closest('a')) continue

    const text = node.textContent || ''
    const frag = document.createDocumentFragment()
    let lastIndex = 0
    let match: RegExpExecArray | null

    URL_REGEX.lastIndex = 0
    while ((match = URL_REGEX.exec(text)) !== null) {
      if (match.index > lastIndex) {
        frag.appendChild(document.createTextNode(text.slice(lastIndex, match.index)))
      }
      const a = document.createElement('a')
      a.href = match[0]
      a.target = '_blank'
      a.rel = 'noopener noreferrer'
      a.textContent = match[0]
      frag.appendChild(a)
      lastIndex = match.index + match[0].length
    }

    // No URLs found in this text node -- skip replacement
    if (lastIndex === 0) continue

    if (lastIndex < text.length) {
      frag.appendChild(document.createTextNode(text.slice(lastIndex)))
    }

    node.replaceWith(frag)
  }

  return container.innerHTML
}

/**
 * Strip all HTML tags and return plain text
 */
export function stripHtml(html: string): string {
  if (!html) return ''

  // Create a temporary element to parse HTML
  const tmp = document.createElement('div')
  tmp.innerHTML = DOMPurify.sanitize(html)

  tmp.querySelectorAll('br').forEach((br) => br.replaceWith('\n'))
  tmp.querySelectorAll('p, div, h1, h2, h3, h4, h5, h6, li, blockquote, tr').forEach((node) => {
    node.appendChild(document.createTextNode('\n'))
  })
  tmp.querySelectorAll('ul, ol').forEach((node) => {
    node.appendChild(document.createTextNode('\n'))
  })

  return (tmp.textContent || tmp.innerText || '')
    .replace(/\r\n?/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

export default sanitizeHtml
